import { Mail, MapPin, Phone, Info } from 'lucide-react';
import type { Locale } from '@/lib/i18n/config';
import type { Dictionary } from '@/lib/i18n/dictionaries';
import type { SiteSettings } from '@/lib/supabase/types';
import { ContactForm } from '@/components/site/forms/contact-form';
import { Reveal } from '@/components/site/reveal';

export function ContactSection({
  locale,
  dict,
  settings,
  compact = false,
}: {
  locale: Locale;
  dict: Dictionary;
  settings: SiteSettings | null;
  compact?: boolean;
}) {
  return (
    <section id="contacto" className="section-pad bg-background">
      <div className="container-tight">
        {!compact && (
          <Reveal className="mb-10 flex items-start gap-3 sm:items-center sm:gap-4">
            <span className="shrink-0 font-serif text-3xl font-bold leading-none text-accent/20 sm:text-5xl">{dict.contact.sectionTitle}</span>
            <div className="min-w-0">
              <span className="eyebrow">{dict.contact.title}</span>
              <h2 className="mt-1 font-serif text-2xl font-bold text-primary sm:text-3xl md:text-4xl">{dict.contact.title}</h2>
            </div>
          </Reveal>
        )}

        <div className="grid gap-12 md:grid-cols-[1fr_1.15fr]">
          <Reveal>
            <p className="text-lg text-muted-foreground">{dict.contact.subtitle}</p>

            <div className="mt-8 space-y-5">
              {settings?.address && (
                <InfoRow icon={<MapPin className="h-5 w-5" />} label={dict.contact.address} value={settings.address} />
              )}
              {settings?.phone && (
                <InfoRow icon={<Phone className="h-5 w-5" />} label={dict.contact.phone} value={settings.phone} />
              )}
              {settings?.email && (
                <InfoRow icon={<Mail className="h-5 w-5" />} label={dict.contact.sendUs} value={settings.email} />
              )}
            </div>

            <div className="mt-8 flex items-start gap-3 rounded-xl border border-accent/20 bg-accent/5 p-4">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
              <p className="text-sm text-muted-foreground">{dict.contact.judicialNote}</p>
            </div>
          </Reveal>

          <Reveal delay={120} className="rounded-2xl border border-border bg-card p-6 md:p-8 shadow-sm">
            <ContactForm locale={locale} dict={dict} />
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function InfoRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-start gap-4">
      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-accent/10 text-accent">{icon}</div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
        <p className="mt-0.5 font-medium text-primary">{value}</p>
      </div>
    </div>
  );
}
